import { displayLocale } from './displayLocale';

/**
 * Derives a dayjs-compatible numeric date format (e.g. 'MM/DD/YYYY', 'DD.MM.YYYY',
 * 'YYYY-MM-DD') from a locale, defaulting to the browser locale.
 *
 * Used to localize date *order and separators* in pickers while the rest of the UI
 * (calendar month/weekday names, all messages) stays English — the app ships with a
 * hardcoded `en` dayjs/IntlProvider locale on purpose.
 */
export function localeDateFormat(locale?: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(2000, 11, 31));

  return parts
    .map((part) => {
      switch (part.type) {
        case 'day':
          return 'DD';
        case 'month':
          return 'MM';
        case 'year':
          return 'YYYY';
        default:
          // Escape alphabetic literals (e.g. Chinese date suffixes) so dayjs
          // doesn't interpret them as format tokens.
          return /[a-zA-Z]/.test(part.value) ? `[${part.value}]` : part.value;
      }
    })
    .join('');
}

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

/** Formats display-only dates in the browser locale (see {@link displayLocale}). */
export function formatDisplayDateTime(value: number | Date, options: Intl.DateTimeFormatOptions): string {
  return formatLocaleDateTime(value, displayLocale, options);
}

/** Formats display-only dates with a cached locale formatter. */
export function formatLocaleDateTime(value: number | Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = dateTimeFormatters.get(key);
  if (!formatter) {
    // Invalid navigator.language values fall back to en-US, mirroring bigUtils' DISPLAY_SEPARATORS.
    try {
      formatter = new Intl.DateTimeFormat(locale, options);
    } catch {
      formatter = new Intl.DateTimeFormat('en-US', options);
    }
    dateTimeFormatters.set(key, formatter);
  }
  return formatter.format(value);
}
